import {useRef, useEffect} from 'react'
import {useVisibleColumns, useGroupedColumn, usePinnedColumns, useUnpinnedColumns, useCache} from '../'
import {getCoreRowModel, getFilteredRowModel, getExpandedRowModel, getGroupedRowModel, getSortedRowModel} from '@tanstack/react-table'
import {onPinnedColumnOrderChange} from '../../actions'

export const useCachedUserPreferences = TABLE_INFO => {
	const {tableName, fields: allTableColumns, defaultVisibleColumns} = TABLE_INFO
	const visibleColumns = useVisibleColumns({tableName, allTableColumns, defaultVisibleColumns})
	const pinnedColumns = usePinnedColumns({tableName})
	const unpinnedColumns = useUnpinnedColumns({tableName})
	const groupedColumn = useGroupedColumn({tableName})
	const cachedSorting = useCache({tableName}).sorting

	return {visibleColumns, pinnedColumns, unpinnedColumns, groupedColumn, cachedSorting}
}

export const columnVisibilityObject = ({allTableColumns, visibleColumns}) => {
	// Create an object which defines each column as being or not being visible
	const object = {}
	allTableColumns.each(column => (object[column] = visibleColumns.includes(column)))
	return object
}

export const getRowId = (shift, idx, parent) => {
	if (!parent) return `${shift.id}-${shift.user_id}`
	return shift?.id?.toString() || idx
}

export const SETTINGS = {
	getExpandedRowModel: getExpandedRowModel(),
	getGroupedRowModel: getGroupedRowModel(),
	getCoreRowModel: getCoreRowModel(),
	getFilteredRowModel: getFilteredRowModel(),
	getSortedRowModel: getSortedRowModel(),
}

/**
 * Persist the pinned-column order **only** when the user reorders it.
 * - Skips the first render (where Redux vs. table state often differ).
 * - Triggers on `columnPinning.left` changes only, avoiding write loops.
 */
export function usePersistPinnedOrder({tableName, pinnedColumns, columnPinning}) {
	const cache = useCache({tableName})
	const didMountRef = useRef(false)
	const prevLeftRef = useRef(columnPinning.left)

	// cache the initial pinned column using onPinnedColumnOrderChange on resetTable
	useEffect(() => {
		onPinnedColumnOrderChange({pinnedColumns, columnPinning, tableName})
	}, [cache.resetTable])

	useEffect(() => {
		const prev = prevLeftRef.current
		const curr = columnPinning.left

		const arraysEqual =
			prev === curr || (Array.isArray(prev) && Array.isArray(curr) && prev.length === curr.length && prev.every((v, i) => v === curr[i]))

		if (didMountRef.current && !arraysEqual) {
			// Only now do we compare vs the cached value and potentially write.
			onPinnedColumnOrderChange({pinnedColumns, columnPinning, tableName})
		}

		didMountRef.current = true
		prevLeftRef.current = curr

		// Intentionally exclude `pinnedColumns` to avoid a Redux->effect->write loop.
		// We still *read* it inside, but only act when `left` actually changed.
	}, [columnPinning.left, tableName])
}
